// ContentVolumePicker — the 13-option "what to generate" grid shown on the creator.
// Groups:
//   • Essentials (always free): slides, study guide, vocabulary, MC quiz, flashcards
//   • Deeper assessments (Pro / beta): pop quiz, short answers, fill-in-the-blank, recall,
//     discussion topics, critical thinking, legal/ethical, eye to the future
import { Check, Lock, Sparkles, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const COMPONENTS = [
  // --- Essentials (free) ---
  { id: "slides",       group: "essentials", label: "PowerPoint slides",   hint: "6-50 slides with notes" },
  { id: "study_guide",  group: "essentials", label: "Printable study guide", hint: "Summary + concepts" },
  { id: "vocabulary",   group: "essentials", label: "Vocabulary + definitions", hint: "Searchable glossary" },
  { id: "mc_quiz",      group: "essentials", label: "Multiple-choice test", hint: "8-100 Qs with grading" },
  { id: "flashcards",   group: "essentials", label: "Flash cards",         hint: "Tap-to-flip study deck" },
  // --- Deeper assessments (pro) ---
  { id: "pop_quiz",         group: "pro", label: "Pop Quiz (teacher key)", hint: "Instructor-only answer sheet" },
  { id: "short_answers",    group: "pro", label: "Short-answer questions", hint: "Open-ended + rubric" },
  { id: "fill_blanks",      group: "pro", label: "Fill-in-the-blank",      hint: "Cloze items + word bank" },
  { id: "recall",           group: "pro", label: "Recall questions",       hint: "Quick retrieval drills" },
  { id: "discussion",       group: "pro", label: "Discussion topics",      hint: "With model talking points" },
  { id: "critical_thinking", group: "pro", label: "Critical thinking",     hint: "Higher-order prompts" },
  { id: "legal_ethical",    group: "pro", label: "Legal & ethical issues", hint: "Scenarios + implications" },
  { id: "eye_to_future",    group: "pro", label: "Eye to the Future",      hint: "5-10 year outlook" },
];

export const ContentVolumePicker = ({ components, onToggle, user }) => {
  const isPro = user?.plan === "pro";
  const essentials = COMPONENTS.filter((c) => c.group === "essentials");
  const pros = COMPONENTS.filter((c) => c.group === "pro");

  const Card = ({ c }) => {
    const picked = components.includes(c.id);
    const locked = c.group === "pro" && !isPro;
    const handleClick = () => {
      if (locked) {
        toast.info("Pro unlocks this — grab a beta code or upgrade from Pricing.");
        return;
      }
      // Prevent unchecking all 5 essentials — keep lecture viable.
      if (picked && c.group === "essentials") {
        const stillPicked = components.filter((x) => x !== c.id && essentials.some((e) => e.id === x));
        if (stillPicked.length === 0) {
          toast.info("Keep at least one essential — your lecture needs it.");
          return;
        }
      }
      onToggle(c.id);
    };
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`component-${c.id}`}
        aria-pressed={picked}
        aria-disabled={locked}
        className={`relative rounded-xl border-2 px-3 py-2.5 text-left transition group
          ${picked && !locked ? "border-accent bg-accent/10"
            : locked ? "border-dashed border-border bg-secondary/30 opacity-70 cursor-not-allowed"
            : "border-border hover:border-accent/40 bg-background"}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{c.label}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{c.hint}</p>
          </div>
          <span className={`shrink-0 h-5 w-5 rounded-full grid place-items-center transition
            ${picked && !locked ? "bg-accent text-accent-foreground"
              : locked ? "bg-secondary text-muted-foreground"
              : "bg-secondary text-muted-foreground group-hover:bg-accent/20"}`}>
            {locked ? <Lock className="h-3 w-3" /> : picked ? <Check className="h-3 w-3" /> : null}
          </span>
        </div>
      </button>
    );
  };

  const pickedCount = components.length;
  const extraPicked = components.filter((id) => pros.some((p) => p.id === id)).length;

  return (
    <section data-testid="content-volume-picker" className="rounded-2xl border border-border bg-card p-5 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl font-bold flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-accent" /> What to include
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick the components you want generated. The lecture + narration are always built.
          </p>
        </div>
        <div className="rounded-full bg-secondary/60 px-3 py-1 text-xs font-semibold">
          <span data-testid="components-picked-count">{pickedCount}</span> selected
          {extraPicked > 0 && <span className="ml-1 text-accent"> · +{extraPicked} Pro</span>}
        </div>
      </header>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Essentials</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {essentials.map((c) => <Card key={c.id} c={c} />)}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
          Deeper assessments
          {!isPro && <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent px-1.5 py-0.5 text-[9px] font-bold normal-case tracking-normal">
            <Sparkles className="h-2.5 w-2.5" /> PRO
          </span>}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {pros.map((c) => <Card key={c.id} c={c} />)}
        </div>
        {!isPro && (
          <p className="mt-3 text-xs text-muted-foreground">
            Have a beta code? Redeem it on <a href="/pricing" className="font-semibold text-accent hover:underline">Pricing → Have a code?</a> to unlock everything here.
          </p>
        )}
      </div>
    </section>
  );
};

export default ContentVolumePicker;
